<template>
  <aside
    v-if="activeProject"
    class="fixed top-0 right-0 h-full w-[360px] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-lg z-40 flex flex-col"
    style="will-change: transform;"
  >
    <!-- Header with font size toggle -->
    <div class="flex items-center justify-between gap-3 p-4 border-b border-gray-100 dark:border-gray-800 bg-blue-50 dark:bg-blue-900/30">
      <div class="flex items-center gap-3">
        <div class="bg-blue-600 rounded-full p-2">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 20h9" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m0 0H3" />
          </svg>
        </div>
        <div>
          <h2 class="text-lg font-bold text-blue-900 dark:text-blue-100">Diligint Agent</h2>
          <div class="text-xs text-gray-500 dark:text-gray-400">
            <span v-if="agentStore.isSessionChat">Research Session: <span class="font-semibold">{{ activeProject.name || 'Untitled' }}</span></span>
            <span v-else>Project: <span class="font-semibold">{{ activeProject.name || 'Untitled' }}</span></span>
          </div>
        </div>
      </div>
      <!-- Controls -->
      <div class="flex items-center gap-2">
        <!-- Chat Mode Toggle -->
        <button
          @click="agentStore.toggleRAG()"
          :class="[
            'px-2 py-1 text-xs rounded transition-colors',
            agentStore.isRAGMode 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
          ]"
          :title="getModeDescription()"
        >
          {{ getModeLabel() }}
        </button>
        <!-- Font size selector -->
        <select 
          v-model="agentStore.fontSize" 
          class="text-xs rounded-lg px-2 py-1 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none"
        >
          <option value="sm">A-</option>
          <option value="md">A</option>
          <option value="lg">A+</option>
        </select>
      </div>
    </div>
    
    <!-- Chat Body at bottom with dynamic font size -->
    <div class="flex-1 flex flex-col-reverse overflow-y-auto p-4 space-y-4 space-y-reverse"
      :class="{
        'text-[13px] leading-6': agentStore.fontSize === 'sm',
        'text-[15px] leading-7': agentStore.fontSize === 'md',
        'text-[17px] leading-8': agentStore.fontSize === 'lg'
      }"
    >
      <div>
        <!-- Messages -->
        <div v-for="(msg, idx) in agentStore.messages" :key="msg.id"
          :class="[
            'flex',
            msg.role === 'user' ? 'justify-end' : 'justify-start',
            idx > 0 ? (msg.role !== agentStore.messages[idx-1].role ? 'mt-6' : 'mt-3') : ''
          ]">
          <div
            :class="[
              'px-5 py-3 rounded-2xl max-w-[75%]',
              msg.role === 'user'
                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-md mr-2 shadow-lg'
                : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-md ml-2 shadow-xl',
              'transition-all duration-150',
              'border border-transparent',
              msg.role === 'user' ? 'border-blue-200' : 'border-gray-200 dark:border-gray-700',
              msg.type === 'error' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : ''
            ]"
            style="word-break:break-word;"
          >
            <!-- Message content -->
            <div class="mb-2">{{ msg.content }}</div>
            
            <!-- File info -->
            <div v-if="msg.type === 'file'" class="text-xs opacity-75 mt-1">
              📎 {{ msg.file?.name }} ({{ formatFileSize(msg.file?.size) }})
            </div>
            
            <!-- Confidence score for AI responses -->
            <div v-if="msg.role === 'assistant' && msg.confidence" class="text-xs opacity-75 mt-2">
              Confidence: {{ Math.round(msg.confidence * 100) }}%
              <span v-if="msg.metadata?.directRetrievalUsed" class="ml-2 text-green-600 dark:text-green-400">(Direct Retrieval)</span>
            </div>
            
            <!-- Sources for RAG responses -->
            <div v-if="msg.sources?.length" class="mt-3 border-t border-gray-300 dark:border-gray-600 pt-2">
              <div class="text-xs font-medium mb-2">
                Sources ({{ msg.sources.length }})
                <span v-if="msg.metadata?.relevantVectorsFound" class="ml-1 text-gray-500">
                  • {{ msg.metadata.relevantVectorsFound }} vectors found
                </span>
              </div>
              <div class="space-y-1">
                <div v-for="source in msg.sources.slice(0, 3)" :key="source.sourceText" 
                     class="text-xs p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                  <div class="font-medium">{{ source.articleTitle }}</div>
                  <div class="opacity-75">{{ source.category }} • {{ Math.round(source.similarity * 100) }}% relevant</div>
                </div>
                <div v-if="msg.sources.length > 3" class="text-xs text-gray-500 p-1">
                  +{{ msg.sources.length - 3 }} more sources
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Loading indicator - appears at bottom in reversed layout -->
        <div v-if="agentStore.isLoading" class="flex justify-start mt-4">
          <div class="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-2xl rounded-bl-md ml-2 px-5 py-3 shadow-xl">
            <div class="flex items-center gap-2">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span class="text-gray-600 dark:text-gray-400">Thinking...</span>
            </div>
          </div>
        </div>
        
        <!-- Empty state -->
        <div v-if="!agentStore.hasMessages" class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-gray-600 dark:text-gray-300 text-center">
          <div class="mb-2">
            <svg class="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
          </div>
          <span>How can I help you with <span class="font-semibold">{{ activeProject.name || 'this project' }}</span>?</span>
          <div class="text-xs mt-2 opacity-75">
            {{ getModeDescription() }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Error display -->
    <div v-if="agentStore.error" class="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
      <div class="text-red-700 dark:text-red-300 text-sm">
        {{ agentStore.error }}
        <button @click="agentStore.error = null" class="ml-2 text-red-500 hover:text-red-700">×</button>
      </div>
    </div>
    
    <!-- Chat Input -->
    <form @submit.prevent="sendMessage" class="flex items-center gap-1 border-t border-gray-100 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
      <div class="flex w-full items-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2 shadow-sm">
        <textarea
          v-model="message"
          placeholder="Type your message..."
          rows="1"
          @input="autoGrow($event)"
          @keydown.enter.exact.prevent="sendMessage"
          class="flex-1 px-4 py-2 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 resize-none overflow-hidden"
          ref="messageInput"
          :disabled="agentStore.isLoading"
        />
        <label class="flex items-center cursor-pointer">
          <input type="file" @change="handleFileUpload" class="hidden" ref="fileInput" />
          <span class="p-2 bg-white dark:bg-gray-900 rounded-lg text-gray-500 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition border border-gray-200 dark:border-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 10-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486" />
            </svg>
          </span>
        </label>
        <button
          type="submit"
          :disabled="!message.trim() || agentStore.isLoading"
          class="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full font-semibold transition shadow flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-5 h-5 mx-auto">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </form>
  </aside>
</template>

<script setup>
import { ref, defineProps, nextTick, watch, onMounted } from 'vue'
import { useAgentStore, CHAT_MODES } from '@/stores/agent'
import { useRoute } from 'vue-router'

const props = defineProps({
  activeProject: {
    type: Object,
    default: null
  }
})

const route = useRoute()
const agentStore = useAgentStore()

const message = ref('')
const fileInput = ref(null)
const messageInput = ref(null)
const MAX_LINES = 4

// Initialize chat when component mounts or project changes
onMounted(() => {
  initializeChat()
})

watch(() => props.activeProject, () => {
  initializeChat()
})

function initializeChat() {
  if (props.activeProject) {
    const projectId = props.activeProject.id || route.query.projectId
    const sessionId = route.query.sessionId || null
    agentStore.initializeChat(projectId, sessionId)
  }
}

async function sendMessage() {
  if (!message.value.trim() || agentStore.isLoading) return
  
  // Store the message and clear immediately
  const messageToSend = message.value.trim()
  message.value = ''
  nextTick(() => resetTextarea())
  
  try {
    await agentStore.sendMessage(messageToSend)
  } catch (error) {
    console.error('Failed to send message:', error)
  }
}

function autoGrow(e) {
  const el = messageInput.value
  if (!el) return
  el.style.height = 'auto'
  const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 24
  const maxHeight = lineHeight * MAX_LINES
  el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px'
}

function resetTextarea() {
  const el = messageInput.value
  if (!el) return
  el.style.height = 'auto'
}

async function handleFileUpload(e) {
  const file = e.target.files[0]
  if (!file) return
  
  try {
    await agentStore.uploadFile(file)
    // Reset file input for next upload
    fileInput.value.value = ''
  } catch (error) {
    console.error('Failed to upload file:', error)
    // Reset file input on error too
    fileInput.value.value = ''
  }
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function getModeLabel() {
  if (agentStore.currentSessionId) {
    // In session context - show whether we're using session mode or not
    return agentStore.isSessionChat ? 'SESSION' : 'GENERAL'
  }
  
  // Not in session context - show RAG mode
  switch (agentStore.chatMode) {
    case CHAT_MODES.BASIC:
      return 'BASIC'
    case CHAT_MODES.RAG_SIMPLE:
      return 'RAG'
    case CHAT_MODES.RAG_ADVANCED:
      return 'RAG+'
    default:
      return 'RAG+'
  }
}

function getModeDescription() {
  if (agentStore.currentSessionId) {
    // In session context
    if (agentStore.isSessionChat) {
      return 'Session mode - Using research data specific to this session'
    } else {
      return 'General mode - Project-wide conversations (not session-specific)'
    }
  }
  
  // Not in session context - show RAG descriptions
  switch (agentStore.chatMode) {
    case CHAT_MODES.BASIC:
      return 'Basic mode - General AI assistance without research context'
    case CHAT_MODES.RAG_SIMPLE:
      return 'RAG mode - Using research context'
    case CHAT_MODES.RAG_ADVANCED:
      return 'Advanced RAG mode - Full context with source attribution'
    default:
      return 'Advanced RAG mode - Full context with source attribution'
  }
}
</script>